import { useQuery } from "@tanstack/react-query";
import { Cpu, KeyRound, Sparkles, ToggleLeft, ToggleRight } from "lucide-react";
import { platformApi, type ConfigResponse, type LicenseStatus } from "@/lib/api";

/**
 * Settings page — read-only runtime introspection.
 *
 * <p>Sections:
 * <ul>
 *   <li><strong>LLM provider</strong> — current provider name (or
 *       "fallback" when none is configured); the explainer falls back
 *       to a deterministic rationale either way.</li>
 *   <li><strong>License</strong> — full status pulled from
 *       {@code /api/v1/license/status}. Shown here in addition to the
 *       header badge so the operator can see the hardware fingerprint
 *       at a glance.</li>
 *   <li><strong>Feature flags</strong> — per-feature on/off, derived
 *       from environment + bean availability. Useful for diagnosing
 *       "why is the scope picker greyed out" without shelling into
 *       the container.</li>
 * </ul>
 */
export function SettingsPage() {
  const config = useQuery({
    queryKey: ["curate-config"],
    queryFn: async () => (await platformApi.get<ConfigResponse>("/config")).data,
    staleTime: 60_000,
    retry: false,
  });
  const license = useQuery({
    queryKey: ["license-status"],
    queryFn: async () => (await platformApi.get<LicenseStatus>("/license/status")).data,
    staleTime: 5 * 60_000,
    retry: false,
  });

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6">
      <h1 className="font-display text-2xl font-semibold text-curate-ink">Settings</h1>
      <p className="text-sm text-curate-slate">
        Read-only summary of how Curate is running on this host. Use the
        details here to confirm a deployment is wired correctly before
        approving any plan items.
      </p>

      <div data-tour-id="settings-llm">
      <Section icon={<Cpu size={14} />} title="LLM provider">
        <Row label="Active provider" value={config.data?.llmProvider ?? "—"} />
        <Row label="Product version" value={config.data?.productVersion ?? "—"} mono />
        {config.data?.llmProvider === "fallback" && (
          <p className="mt-2 rounded-md bg-curate-mist px-3 py-2 text-2xs text-curate-slate">
            No LLM configured — Curate's <code>/explain</code> endpoint will return a
            deterministic rationale derived from the planner's reason
            list. Set <code>CLOUDCRAFT_LLM_PROVIDER</code> + the matching
            API key in your <code>.env</code> to enable LLM-rendered
            rationales.
          </p>
        )}
      </Section>
      </div>

      <Section icon={<KeyRound size={14} />} title="License">
        {license.isError && (
          <p className="text-sm text-curate-slate">
            License gate disabled on this deployment.
          </p>
        )}
        {license.data && (
          <>
            <Row
              label="Status"
              value={license.data.ok ? "Valid" : `Invalid: ${license.data.failureReason ?? "unknown"}`}
              tone={license.data.ok ? "ok" : "bad"}
            />
            {license.data.ok && (
              <>
                <Row label="Issued to" value={license.data.customerName ?? "—"} />
                <Row
                  label="Expires"
                  value={`${formatDate(license.data.expiresAt)} (${license.data.daysRemaining}d)`}
                  tone={license.data.daysRemaining <= 14 ? "warn" : undefined}
                />
                <Row label="Issued" value={formatDate(license.data.issuedAt)} />
              </>
            )}
            {!license.data.ok && license.data.failureMessage && (
              <p className="mt-2 rounded-md bg-curate-danger/10 px-3 py-2 text-2xs text-curate-danger">
                {license.data.failureMessage}
              </p>
            )}
            <Row label="Hardware fingerprint" value={license.data.fingerprint} mono />
          </>
        )}
      </Section>

      <Section icon={<Sparkles size={14} />} title="Feature flags">
        {config.data?.featureFlags ? (
          <ul className="space-y-1.5">
            {Object.entries(config.data.featureFlags).map(([flag, on]) => (
              <li key={flag} className="flex items-center justify-between text-sm">
                <span className="text-curate-ink">{flag}</span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-medium ${
                    on
                      ? "bg-curate-success/10 text-curate-success"
                      : "bg-curate-slate/10 text-curate-slate"
                  }`}
                >
                  {on ? <ToggleRight size={11} /> : <ToggleLeft size={11} />}
                  {on ? "on" : "off"}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-curate-slate">Loading feature flags…</p>
        )}
      </Section>
    </div>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-curate-slate/20 bg-white p-4 shadow-card">
      <h2 className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-curate-ink">
        {icon}
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: "ok" | "warn" | "bad";
}) {
  const valueClass =
    tone === "ok"
      ? "text-curate-success"
      : tone === "warn"
      ? "text-curate-warn"
      : tone === "bad"
      ? "text-curate-danger"
      : "text-curate-ink";
  return (
    <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 py-1 text-sm">
      <dt className="text-curate-slate">{label}</dt>
      <dd
        className={`text-right font-medium ${valueClass} ${
          mono ? "break-all font-mono text-2xs" : ""
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
